// @ts-check

import { html } from "lit-html";
import {
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
  renderDetailRow,
  renderMetricTile,
  renderSectionCard,
  renderTextareaField,
} from "../ui.js";
import { copyTextToClipboard, hasText } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createWordCountState>} WordCountState */

const ICON_BUTTON_CONTENT_CLASS =
  "inline-flex min-w-0 items-center justify-center gap-1.5";

export const wordCountTool = {
  key: "wordCount",
  path: "/word-count",
  title: "Word Count",
  blurb: "Count words, characters, paragraphs, sentences, and reading time.",
  badge: "Text",
  accentClass: "bg-gradient-to-r from-cyan-300 via-sky-300 to-indigo-300",
  createState: createWordCountState,
  render: renderWordCountPage,
};

function createWordCountState() {
  return {
    text: "",
    copyStatus: "idle",
  };
}

/**
 * @param {WordCountState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderWordCountPage(toolState, env) {
  const stats = computeWordCountStats(toolState.text);
  const hasTextValue = hasText(toolState.text);
  const copyButtonClass = getCopyButtonClass(toolState.copyStatus);
  const copyButtonLabel = getCopyButtonLabel(toolState.copyStatus);

  return html`
    <section
      class="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] xl:items-start"
    >
      ${renderSectionCard(
        "Word counter",
        "Paste text, write from scratch, clear it, or copy it back out.",
        html`
          ${renderTextareaField({
            label: "Text",
            value: toolState.text,
            placeholder: "Start typing or paste your text here...",
            onInput: function (event) {
              env.handleFieldInput(toolState, "text", event);
            },
          })}

          <div class="mt-4 flex flex-wrap gap-3">
            <button
              class=${SECONDARY_BUTTON_CLASS}
              type="button"
              @click=${function () {
                toolState.text = "";
                toolState.copyStatus = "idle";
                env.scheduleRender();
              }}
            >
              <span class=${ICON_BUTTON_CONTENT_CLASS}>
                ${renderClearIcon()}
                <span>Clear</span>
              </span>
            </button>
            <button
              class=${copyButtonClass}
              type="button"
              ?disabled=${!hasTextValue}
              @click=${async function () {
                if (!hasTextValue) {
                  return;
                }

                try {
                  await copyTextToClipboard(toolState.text);
                  env.setTemporaryStatus(
                    toolState,
                    "copyStatus",
                    "copied",
                    "idle",
                    "wordCount",
                  );
                } catch (error) {
                  console.error("[app] copy text failed", error);
                  env.setTemporaryStatus(
                    toolState,
                    "copyStatus",
                    "error",
                    "idle",
                    "wordCount",
                  );
                }
              }}
            >
              <span class=${ICON_BUTTON_CONTENT_CLASS}>
                ${renderCopyIcon()}
                <span>${copyButtonLabel}</span>
              </span>
            </button>
          </div>
        `,
      )}

      <div class="grid gap-4">
        <section class="grid gap-4 sm:grid-cols-2">
          ${renderMetricTile(
            String(stats.wordCount),
            "Words",
            "border-sky-300/20 bg-sky-300/5 text-sky-200",
          )}
          ${renderMetricTile(
            String(stats.characterCount),
            "Characters",
            "border-emerald-300/20 bg-emerald-300/5 text-emerald-200",
          )}
          ${renderMetricTile(
            String(stats.nonSpaceCount),
            "Non-space characters",
            "border-violet-300/20 bg-violet-300/5 text-violet-200",
          )}
          ${renderMetricTile(
            String(stats.paragraphCount),
            "Paragraphs",
            "border-amber-300/20 bg-amber-300/5 text-amber-200",
          )}
        </section>

        ${renderSectionCard(
          "Statistics",
          "More useful derived metrics for drafts and pasted text.",
          html`
            <div
              class="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]"
            >
              ${renderDetailRow("Sentences", String(stats.sentenceCount))}
              ${renderDetailRow(
                "Average words per sentence",
                String(stats.avgWordsPerSentence),
              )}
              ${renderDetailRow("Reading time", stats.readingTime)}
              ${renderDetailRow(
                "Characters including spaces",
                String(stats.characterCount),
              )}
            </div>
          `,
        )}
      </div>
    </section>
  `;
}

/**
 * @returns {LitView}
 */
function renderCopyIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
      />
    </svg>
  `;
}

/**
 * @returns {LitView}
 */
function renderClearIcon() {
  return html`
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1.5"
      stroke="currentColor"
      class="size-6 shrink-0"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  `;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonClass(copyStatus) {
  let result = PRIMARY_BUTTON_CLASS;
  if (copyStatus === "copied") {
    result =
      "inline-flex h-11 items-center justify-center rounded-lg bg-emerald-700 px-4 text-sm font-semibold text-white";
  }

  if (copyStatus === "error") {
    result =
      "inline-flex h-11 items-center justify-center rounded-lg bg-red-600 px-4 text-sm font-semibold text-white";
  }

  return result;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getCopyButtonLabel(copyStatus) {
  let result = "Copy text";
  if (copyStatus === "copied") {
    result = "Copied";
  }

  if (copyStatus === "error") {
    result = "Copy failed";
  }

  return result;
}

/**
 * @param {string} text
 * @returns {{ wordCount: number, characterCount: number, nonSpaceCount: number, paragraphCount: number, sentenceCount: number, avgWordsPerSentence: number, readingTime: string }}
 */
function computeWordCountStats(text) {
  const wordCount = countWords(text);
  const characterCount = text.length;
  const nonSpaceCount = text.replace(/\s/g, "").length;
  const paragraphCount = countParagraphs(text);
  const sentenceCount = countSentences(text);
  const avgWordsPerSentence =
    sentenceCount === 0 ? 0 : Math.round((wordCount / sentenceCount) * 10) / 10;
  const readingTime = getReadingTimeLabel(wordCount);

  return {
    wordCount,
    characterCount,
    nonSpaceCount,
    paragraphCount,
    sentenceCount,
    avgWordsPerSentence,
    readingTime,
  };
}

/**
 * @param {string} text
 * @returns {number}
 */
function countWords(text) {
  if (!hasText(text)) {
    return 0;
  }

  const words = text
    .trim()
    .split(/\s+/)
    .filter(function (word) {
      return word.length > 0;
    });
  return words.length;
}

/**
 * @param {string} text
 * @returns {number}
 */
function countParagraphs(text) {
  if (!hasText(text)) {
    return 0;
  }

  const paragraphs = text
    .trim()
    .split(/\n\s*\n/)
    .filter(function (paragraph) {
      return paragraph.trim().length > 0;
    });
  return paragraphs.length;
}

/**
 * @param {string} text
 * @returns {number}
 */
function countSentences(text) {
  if (!hasText(text)) {
    return 0;
  }

  const sentences = text
    .trim()
    .split(/[.!?]+\s+|[.!?]+$/)
    .filter(function (sentence) {
      return sentence.trim().length > 0;
    });
  return sentences.length;
}

/**
 * @param {number} wordCount
 * @returns {string}
 */
function getReadingTimeLabel(wordCount) {
  if (wordCount === 0) {
    return "0m";
  }

  const wordsPerMinute = 200;
  const minutes = wordCount / wordsPerMinute;
  if (minutes < 1) {
    return "< 1m";
  }

  return `${Math.ceil(minutes)}m`;
}
